import React, { createContext, useContext, useEffect, useState } from "react";

import { useAckGooseGameDashboardCatchUpMutation } from "./graphql/ackGooseGameDashboardCatchUp.generated";
import { useMyGooseGameDashboardCatchUpQuery } from "./graphql/hacker/myGooseGameDashboardCatchUp.generated";
import {
  MyGooseGameQuestLogsQuery,
  useMyGooseGameQuestLogsQuery,
} from "./graphql/hacker/myGooseGameQuestLogs.generated";

const LEARN_MORE_SEEN_KEY = "goose-games-learn-more-seen";
const LEARN_MORE_SEEN_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const hasSeenLearnMoreRecently = () => {
  const seenAt = Number(localStorage.getItem(LEARN_MORE_SEEN_KEY));
  return Boolean(seenAt) && Date.now() - seenAt < LEARN_MORE_SEEN_TTL_MS;
};

interface GooseGamesContextType {
  showWelcomeBack: boolean;
  myQuestLogData?: MyGooseGameQuestLogsQuery;
  refetchQuestLogs: () => void;
  handleWelcomeBackClose: () => void;
  showLearnMore: boolean;
  handleLearnMoreOpen: () => void;
  handleLearnMoreClose: () => void;
}

const GooseGamesContext = createContext<GooseGamesContextType>({
  showWelcomeBack: false,
  myQuestLogData: undefined,
  refetchQuestLogs: () => {},
  handleWelcomeBackClose: () => {},
  showLearnMore: false,
  handleLearnMoreOpen: () => {},
  handleLearnMoreClose: () => {},
});

export const useGooseGamesContext = () => useContext(GooseGamesContext);

export const GooseGamesContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { data: myQuestLogData, refetch: refetchQuestLogs } =
    useMyGooseGameQuestLogsQuery();
  const {
    data: catchUpData,
    refetch: refetchMyGooseGameDashboardCatchUp,
    loading: fetchingCatchUp,
  } = useMyGooseGameDashboardCatchUpQuery({ fetchPolicy: "network-only" });
  const [ackGooseGameDashboardCatchUp] =
    useAckGooseGameDashboardCatchUpMutation();

  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [showLearnMore, setShowLearnMore] = useState(
    () => !hasSeenLearnMoreRecently()
  );

  useEffect(() => {
    if (!fetchingCatchUp) {
      const hasUpdates =
        catchUpData?.myGooseGameDashboardCatchUp?.hasUpdates ?? false;
      setShowWelcomeBack(hasUpdates);

      if (hasUpdates) refetchQuestLogs();
    }
  }, [catchUpData, fetchingCatchUp, refetchQuestLogs]);

  const handleWelcomeBackClose = () => {
    setShowWelcomeBack(false);
    ackGooseGameDashboardCatchUp();
    refetchMyGooseGameDashboardCatchUp();
  };

  const handleLearnMoreOpen = () => setShowLearnMore(true);

  const handleLearnMoreClose = () => {
    setShowLearnMore(false);
    localStorage.setItem(LEARN_MORE_SEEN_KEY, Date.now().toString());
  };

  return (
    <GooseGamesContext.Provider
      value={{
        showWelcomeBack,
        myQuestLogData,
        refetchQuestLogs,
        handleWelcomeBackClose,
        showLearnMore,
        handleLearnMoreOpen,
        handleLearnMoreClose,
      }}
    >
      {children}
    </GooseGamesContext.Provider>
  );
};
